import { z } from "zod";
import { continuityDigestSchema, continuityIdentitySchema, workspaceArchiveIdentitySchema, MAX_CONTINUITY_CHUNK_BYTES } from "./continuity.js";
export const CONTINUITY_ARCHIVE_FORMAT = "opencloud-continuity-chunks-v1";
export const MAX_CONTINUITY_ARCHIVE_ENTRIES = 100_000;
export const MAX_CONTINUITY_ARCHIVE_CHUNKS = 262_144;
export const MAX_CONTINUITY_ARCHIVE_BYTES = 1024 ** 4;
export const continuityArchiveRootSchema = z.enum(["workspace", "native", "mutations"]);
export const continuityArchiveIdentitySchema = z.union([continuityIdentitySchema, workspaceArchiveIdentitySchema]);
export const continuityArchivePathSchema = z.string().min(1).max(4_096).refine((value) => !/[\\\x00-\x1f\x7f]/.test(value) &&
    new TextEncoder().encode(value).byteLength <= 4_096 &&
    value.split("/").length <= 128 &&
    value.split("/").every((part) => part !== "" && part !== "." && part !== ".." && !/^[a-z]:/i.test(part)), "Archive paths must be safe relative paths");
export const continuityArchiveChunkSchema = z.object({
    digest: continuityDigestSchema,
    byteLength: z.number().int().positive().max(MAX_CONTINUITY_CHUNK_BYTES),
}).strict();
const archiveEntryBase = {
    root: continuityArchiveRootSchema,
    path: continuityArchivePathSchema,
    mode: z.number().int().min(0).max(0o777),
};
export const continuityArchiveEntrySchema = z.discriminatedUnion("kind", [
    z.object({ ...archiveEntryBase, kind: z.literal("directory") }).strict(),
    z.object({ ...archiveEntryBase, kind: z.literal("symlink"), target: z.string().min(1).max(4_096) }).strict(),
    z.object({
        ...archiveEntryBase,
        kind: z.literal("file"),
        byteLength: z.number().int().nonnegative().max(MAX_CONTINUITY_ARCHIVE_BYTES),
        digest: continuityDigestSchema,
        chunks: z.array(continuityArchiveChunkSchema).max(MAX_CONTINUITY_ARCHIVE_CHUNKS),
    }).strict(),
]);
export const continuityArchiveManifestSchema = z.object({
    schemaVersion: z.literal(1),
    format: z.literal(CONTINUITY_ARCHIVE_FORMAT),
    binding: continuityArchiveIdentitySchema,
    roots: z.array(continuityArchiveRootSchema).min(1).max(3),
    entries: z.array(continuityArchiveEntrySchema).max(MAX_CONTINUITY_ARCHIVE_ENTRIES),
    totalBytes: z.number().int().nonnegative().max(MAX_CONTINUITY_ARCHIVE_BYTES),
}).strict().superRefine((value, context) => {
    const roots = new Set(value.roots);
    if (!("sessionId" in value.binding) && (roots.has("native") || !roots.has("workspace"))) {
        context.addIssue({ code: "custom", message: "Workspace archives contain source roots only" });
    }
    const entries = new Map();
    let totalBytes = 0;
    let totalChunks = 0;
    if (roots.size !== value.roots.length) {
        context.addIssue({ code: "custom", message: "Archive roots must be unique" });
    }
    for (const entry of value.entries) {
        if (!("sessionId" in value.binding) && entry.root === "workspace" &&
            (entry.path === ".opencloud-agent" || entry.path.startsWith(".opencloud-agent/"))) {
            context.addIssue({ code: "custom", message: "Private runtime files are not workspace source" });
        }
        const key = `${entry.root}/${entry.path}`;
        if (!roots.has(entry.root) || entries.has(key)) {
            context.addIssue({ code: "custom", message: "Archive entries must have unique paths under declared roots" });
        }
        entries.set(key, entry);
        if (entry.kind === "file") {
            totalBytes += entry.byteLength;
            totalChunks += entry.chunks.length;
            if (entry.byteLength !== entry.chunks.reduce((total, chunk) => total + chunk.byteLength, 0)) {
                context.addIssue({ code: "custom", message: "File size must match its chunk lengths" });
            }
        }
    }
    for (const entry of value.entries) {
        const parts = entry.path.split("/");
        parts.pop();
        if (parts.length && entries.get(`${entry.root}/${parts.join("/")}`)?.kind !== "directory") {
            context.addIssue({ code: "custom", message: "An entry parent must be an explicit directory" });
        }
    }
    if (totalBytes !== value.totalBytes || totalChunks > MAX_CONTINUITY_ARCHIVE_CHUNKS) {
        context.addIssue({ code: "custom", message: "Archive totals exceed or disagree with their bounds" });
    }
});
//# sourceMappingURL=continuity-archive.js.map